// The room header's BPM reading, editable: tap it, type a tempo, Enter. Held
// locally while typing and proposed once on Enter or blur — a keystroke per
// intent would stale each next one. Clamped to what Live accepts (20–999).
// Read-only (a plain reading) without `onCommit`.

import { useState } from "react";
import { cn } from "@web-shared/lib/utils";

export function TempoReading({
  tempo,
  onCommit,
  className,
}: {
  tempo: number;
  onCommit?: (bpm: number) => void;
  className?: string;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = Math.round(tempo * 100) / 100;
  if (!onCommit) {
    return <div className={cn("font-mono text-lg leading-none", className)}>{Math.round(tempo)}</div>;
  }
  const commit = () => {
    if (draft === null) return;
    const value = Number(draft);
    setDraft(null);
    if (draft.trim() === "" || !Number.isFinite(value)) return;
    const clamped = Math.min(999, Math.max(20, Math.round(value * 100) / 100));
    if (clamped !== shown) onCommit(clamped);
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft ?? String(shown)}
      onFocus={(e) => e.currentTarget.select()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        // The page's shortcuts (Space plays) must not see typing.
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(null);
          e.currentTarget.blur();
        }
      }}
      aria-label="Tempo (BPM)"
      title="Tempo — type a new one and press Enter"
      className={cn(
        "w-[5ch] rounded bg-transparent text-center font-mono text-lg leading-none outline-none hover:bg-white/5 focus:bg-white/10",
        className,
      )}
    />
  );
}
